import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { SubscriptionTierInterface, SubscriptionTiersService } from '@galaxy/partner';
import { filter, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';

export interface UnifiedTermsOfServiceAPIResponse {
  data?: UnifiedTermsOfServiceData;
}

export interface UnifiedTermsOfServiceData {
  unifiedTermsOfService?: string;
  isImpersonated?: boolean;
  hasAcceptedLatestUnifiedTermsOfService?: boolean;
}

@Injectable({ providedIn: 'root' })
export class UnifiedTOSService {
  private getUrl = '/_ajax/v1/unified-terms-of-service/';
  private postUrl = '/_ajax/v1/accept-unified-terms-of-service';
  private unifiedTermsOfServiceData$$: BehaviorSubject<UnifiedTermsOfServiceAPIResponse> = new BehaviorSubject(null);
  private tosDataLoaded$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _tosDataLoaded$ = this.tosDataLoaded$$.asObservable();
  private _unifiedTermsOfServiceData$ = this.unifiedTermsOfServiceData$$.asObservable();

  public readonly hasAcceptedTerms$ = this._tosDataLoaded$.pipe(
    filter(Boolean),
    switchMap(() => this.unifiedTermsOfServiceData$$),
    filter(Boolean),
    map((data) => data?.data?.hasAcceptedLatestUnifiedTermsOfService ?? false),
  );
  private hasAcceptedTerms: boolean;
  public unifiedTermsOfService: string;
  public isImpersonated: boolean;
  public isClassicSubscription: boolean;

  constructor(
    private httpClient: HttpClient,
    public dialog: MatDialog,
  ) {}

  get tosDataLoaded$(): Observable<boolean> {
    return this._tosDataLoaded$;
  }

  get unifiedTermsOfServiceData$(): Observable<UnifiedTermsOfServiceAPIResponse> {
    return this._unifiedTermsOfServiceData$;
  }

  get hasAcceptedTOS(): boolean {
    return this.hasAcceptedTerms;
  }

  set hasAcceptedTOS(newAcceptedValue: boolean) {
    this.hasAcceptedTerms = newAcceptedValue;
  }

  acceptTermsOfService(): Observable<any> {
    return this.httpClient.post(this.postUrl, null);
  }

  reloadTermsOfServiceData(): void {
    const getUrl = this.getUrl + '?loadTerms=true';
    this.loadTermsOfService(getUrl);
  }

  getTermsOfServiceData(subscription: SubscriptionTierInterface): void {
    if (subscription) {
      this.isClassicSubscription = SubscriptionTiersService.isClassicSubscription(subscription);
    }
    this.loadTermsOfService(this.getUrl);
  }

  private loadTermsOfService(getUrl: string): void {
    this.httpClient
      .get<UnifiedTermsOfServiceAPIResponse>(getUrl)
      .pipe(
        tap((tosData) => {
          this.unifiedTermsOfService = tosData?.data?.unifiedTermsOfService;
          this.isImpersonated = tosData?.data?.isImpersonated;
          this.hasAcceptedTerms = tosData?.data?.hasAcceptedLatestUnifiedTermsOfService;
        }),
        shareReplay(1),
        finalize(() => this.tosDataLoaded$$.next(true)),
      )
      .subscribe((tosData) => {
        this.unifiedTermsOfServiceData$$.next(tosData);
      });
  }
}
